import React, { Component } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { AvatarPicker, FormGrid, OptionAutocomplete } from '../../../../ui';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import noavatar from '../../../../public/images/noavatar.png';
import actions from '../../../../redux/actions';
import { OperationStatus } from '../../../../types/OperationStatus';
import { UserRole } from '../../../../types/UserRole';
import countryCodes from '../../../../util/options/countryCodes';
import PasswordChangeModal from '../../../PasswordChangeModal';

const mapStateToProps = (state) => {
  return {
    userUploadAvatarStatus: state.userUploadAvatarStatus,
    userSaveStatus: state.userSaveStatus,
    passwordChangeStatus: state.passwordChangeStatus,
    user: state.user,
    isMobile: state.resolution.device === 'mobile' || state.resolution.device === 'mobile-tablet',
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateUser: (data) => dispatch(actions.updateUser(data)),
    saveUser: (data: any, notify?: boolean) => dispatch(actions.saveUser(data, notify)),
    uploadAvatar: (avatarFile) => dispatch(actions.uploadAvatar(avatarFile)),
  };
};

class Personal extends Component<any> {
  public state: any;

  constructor(props) {
    super(props);

    this.state = {
      newAvatar: {},
      newAvatarError: '',
      errors: [],
    };
  }

  resetErrorState = (event, data) => {
    const name = event.target.name;

    if (this.state.errors.includes(name)) {
      const errors = [...this.state.errors];
      errors.splice(this.state.errors.indexOf(name), 1);
      this.setState({ errors });
    }
  };

  onChange = (event, data) => {
    const user = { ...this.props.user };
    user[data.name] = data.value;

    return this.props.updateUser(user);
  };

  validateInput = (event, data) => {
    const errors = [...this.state.errors];
    const name = data && data.name ? data.name : event.target.name;
    const value = data && data.value ? data.value : event.target.value;

    switch (name) {
      case 'firstName': {
        if (!value.length) {
          errors.push(name);
          this.setState({ errors });
        }
        break;
      }
      case 'lastName': {
        if (!value.length) {
          errors.push(name);
          this.setState({ errors });
        }
        break;
      }
      case 'companyName': {
        if (!value.length) {
          errors.push(name);
          this.setState({ errors });
        }
        break;
      }
      default: {
        break;
      }
    }
  };

  onAvatarUpload = ([acceptedImageFile]) => {
    const newAvatar = Object.assign(acceptedImageFile, {
      preview: URL.createObjectURL(acceptedImageFile),
    });

    this.setState({
      newAvatar,
      newAvatarError: '',
    });
  };

  onAvatarRejected = () => {
    this.setState({ newAvatarError: 'Avatar size can be no more than 1 Mb' });
  };

  onSave = async () => {
    const userData: Record<string, any> = Object.keys(this.props.user).reduce((result, el) => {
      if (
        this.props.user[el] !== null &&
        el !== 'cycles' // cycles are managed separately
      ) {
        result[el] = this.props.user[el];
      }

      return result;
    }, {});

    if (this.state.newAvatar.preview) {
      userData.newAvatar = this.state.newAvatar;
    }

    const errors: any[] = [];

    ['firstName', 'lastName', 'country', 'companyName'].forEach((el) => {
      if (!userData[el]) {
        errors.push(el);
      }
    });

    if (errors.length) {
      return this.setState({ errors });
    }

    delete userData.avatarUrl;

    // Only when a new photo was picked (newAvatar starts as {})
    if (this.state.newAvatar.preview) {
      await this.props.uploadAvatar(this.state.newAvatar);
    }

    this.props.saveUser(userData, true);
  };

  render() {
    const userSaveProcessing = this.props.userSaveStatus.status === OperationStatus.processing || this.props.userUploadAvatarStatus.status === OperationStatus.processing;
    const user = this.props.user;
    const isViewer = user.role === UserRole.Viewer;

    let avatarUrl;

    if (this.state.newAvatar.preview) {
      avatarUrl = this.state.newAvatar.preview;
    } else if (user.avatarUrl) {
      avatarUrl = user.avatarUrl;
    } else {
      avatarUrl = noavatar;
    }

    const errors: string[] = this.state.errors;

    const text = (name: string, label: string, required = false) => (
      <TextField
        name={name}
        label={label}
        required={required}
        value={user[name] ?? ''}
        error={errors.includes(name)}
        onChange={(event) => this.onChange(null, { name, value: event.target.value })}
        onFocus={() => this.resetErrorState({ target: { name } }, null)}
        onBlur={(event) => this.validateInput({ target: { name, value: event.target.value } }, { name, value: event.target.value })}
        fullWidth
      />
    );

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <AvatarPicker
          src={avatarUrl}
          error={this.state.newAvatarError}
          onPick={(file) => this.onAvatarUpload([file])}
          onReject={this.onAvatarRejected}
        />

        <FormGrid isMobile={this.props.isMobile}>
          {text('firstName', 'First name', true)}
          {text('lastName', 'Last name', true)}
          {text('companyName', 'Company name', true)}
          <OptionAutocomplete
            label="Country"
            required
            options={countryCodes}
            value={user.country}
            error={errors.includes('country')}
            onValueChange={(value) => this.onChange(null, { name: 'country', value })}
          />
          {text('companyAddress', 'Company address')}
          {text('companyHqAddress', 'Company HQ address')}
          {text('position', 'Position')}
          {text('companyUrl', 'Company URL')}
        </FormGrid>

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <PasswordChangeModal />
          <Button variant="contained" disabled={userSaveProcessing || errors.length > 0 || isViewer} onClick={this.onSave}>
            {userSaveProcessing ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Box>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Personal));
